/**
 * Журнал «Мост» страницы «Расширение» (`?tab=events&log=bridge`): как работает служебное расширение
 * «Прокат — мост» и ответ из «Чатов» через служебные браузеры. Три части: мосты и их вкладки;
 * каждый ответ с шагами набора (сколько шёл каждый шаг, чем кончилось, подтвердил ли мост, снимок
 * вкладки при отказе); события расширения — ошибки и счётчики приёма.
 * Спека `docs/specs/2026-10-07-ext-bridge-design.md`, раздел «Журнал и телеметрия».
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { DateRangePicker } from "@/components/DateRangePicker";
import { iso, plusDays } from "@/lib/dates";
import {
  extBridgeShot,
  loadExtBridgeActivity,
  loadExtBridgeJournal,
  setExtBridgeSend,
  setExtBridgeUse,
  type ExtBridgeCounts,
  type ExtBridgeEvent,
  type ExtBridgeRow,
  type ExtBridgeSend,
} from "@/lib/extension";

const ПЛОЩАДКА: Record<string, string> = { telegram: "Telegram", max: "MAX", whatsapp: "WhatsApp", "": "расширение" };
const НАБИРАЕТСЯ = { label: "набирается", tone: "border-border bg-bg-subtle text-fg-muted" };
const ИСХОД: Record<string, { label: string; tone: string }> = {
  sent: { label: "ушло", tone: "border-ok/40 bg-ok-bg text-ok" },
  failed: { label: "не ушло", tone: "border-err/40 bg-err-bg text-err" },
  unknown: { label: "не подтверждено", tone: "border-err/40 bg-err-bg text-err" },
};
const ПЕРИОД = [
  { hours: 24, label: "сутки" },
  { hours: 72, label: "3 дня" },
  { hours: 168, label: "неделя" },
];
const th = "px-3 py-2 font-medium";
const head = "bg-bg-subtle text-[11px] uppercase tracking-[0.05em] text-fg-faint";
const sec = (ms: number) => `${(ms / 1000).toFixed(1).replace(".", ",")} с`;
const when = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${iso.slice(11, 19)}`;
const n = (v: unknown) => Number(v) || 0;
/** Сколько минут назад (время базы — московское). */
const agoMin = (iso: string) => Math.max(0, Math.round((Date.now() + 3 * 3600_000 - Date.parse(`${iso}Z`)) / 60_000));

/** Вкладка в работе: приём у моста включён и площадку в этом браузере используем. */
const вРаботе = (r: ExtBridgeRow) => !!r.channel && r.listen && !r.unused;
/** Вкладка в работе, а входа на площадке нет: она отмечается, но сообщений не несёт. */
const безВхода = (r: ExtBridgeRow) => вРаботе(r) && !r.account_id;
/** Имя моста — «win-2025 · 22005»: в сводке хватает порта. */
const порт = (r: ExtBridgeRow) => r.name.split("·").pop()?.trim() || r.install;
/** Номер с тем, как он у нас называется: «Прием 9» +79250474391. */
const номер = (title: string, phone: string) => [title && `«${title}»`, phone && `+${phone}`].filter(Boolean).join(" ");

const ДНИ = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
/** «21.10 вт» — день строки таблицы. */
const деньСтроки = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)} ${ДНИ[new Date(`${d}T12:00:00`).getDay()]}`;
const БЕЗ_РАЗБИВКИ = "До 21.10.26 вкладка сообщала только, сколько всего новых сообщений принесла: от клиентов они или наши, не известно";

const тыс = (v: number) => v.toLocaleString("ru-RU");

function Tile({ label, value, hint, sub, bad }: { label: string; value: number; hint: string; sub?: string | undefined; bad?: boolean }) {
  return (
    <div className="flex min-w-[180px] flex-1 flex-col gap-0.5 rounded-[var(--radius-card)] border border-border px-4 py-3" title={hint}>
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className={`text-[24px] font-semibold tabular-nums leading-tight ${bad && value ? "text-err" : ""}`}>{тыс(value)}</div>
      {sub && <div className="text-[12px] tabular-nums text-fg-muted">{sub}</div>}
    </div>
  );
}

/** Ячейки чисел одной строки: принёс всего с полоской (доля от самой активной строки), от клиентов, наших, ответы. */
function Counts({ c, max }: { c: ExtBridgeCounts; max: number }) {
  const нет = c.added === 0 && c.sent === 0 && c.failed === 0;
  const split = (v: number) =>
    c.unsplit && v === 0 ? (
      <span className="text-fg-faint" title={БЕЗ_РАЗБИВКИ}>
        —
      </span>
    ) : (
      v
    );
  return (
    <>
      <td className={`whitespace-nowrap px-3 py-1.5 tabular-nums ${нет ? "text-fg-faint" : ""}`}>
        <div className="flex items-center gap-2">
          <span className="w-10 text-right">{c.added}</span>
          <span className="h-1.5 w-[90px] overflow-hidden rounded-full bg-bg-subtle" aria-hidden>
            <span className="block h-full rounded-full bg-accent" style={{ width: `${max ? (100 * c.added) / max : 0}%` }} />
          </span>
        </div>
      </td>
      <td className={`whitespace-nowrap px-3 py-1.5 text-right tabular-nums ${нет ? "text-fg-faint" : ""}`}>{split(c.in)}</td>
      <td className={`whitespace-nowrap px-3 py-1.5 text-right tabular-nums ${нет ? "text-fg-faint" : "text-fg-muted"}`}>{split(c.out)}</td>
      <td className={`whitespace-nowrap px-3 py-1.5 text-right tabular-nums ${нет ? "text-fg-faint" : ""}`}>{c.sent}</td>
      <td className={`whitespace-nowrap px-3 py-1.5 text-right tabular-nums ${c.failed ? "font-semibold text-err" : "text-fg-faint"}`}>{c.failed}</td>
    </>
  );
}

function CountsHead() {
  return (
    <>
      <th className={th} title="Новых сообщений, которые вкладка моста завела в «Чаты»">
        Принёс в «Чаты»
      </th>
      <th className={`${th} text-right`} title="Из них от клиентов">
        от клиентов
      </th>
      <th className={`${th} text-right`} title="Из них наших: набранных прямо в браузере и рассылок. Ответы из «Чатов» — в следующей колонке">
        наших из браузера
      </th>
      <th className={`${th} text-right`} title="Ответы из «Чатов», набранные во вкладке моста и увиденные в переписке площадки">
        Ответов ушло
      </th>
      <th className={`${th} text-right`} title="Ответы из «Чатов», которые не ушли или не подтвердились">
        не ушло
      </th>
    </>
  );
}

/**
 * «Активность моста» за выбранный период (`&bfrom=&bto=`, по умолчанию 7 дней): сколько сообщений мост
 * принёс в «Чаты» и сколько ответов из «Чатов» отправил, по дням или по вкладкам (`&bby=tabs`).
 */
function Activity() {
  const [params, setParams] = useSearchParams();
  const today = new Date();
  const from = params.get("bfrom") || iso(plusDays(today, -6));
  const to = params.get("bto") || iso(today);
  const by = params.get("bby") === "tabs" ? "tabs" : "days";
  const set = (patch: Record<string, string>) => {
    const out = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) out.set(k, v);
      else out.delete(k);
    }
    setParams(out, { replace: true });
  };
  const q = useQuery({
    queryKey: ["ext-bridge-activity", from, to],
    queryFn: () => loadExtBridgeActivity(from, to),
    refetchInterval: 60_000,
    placeholderData: (prev) => prev,
  });
  const d = q.data;
  const rows = d ? (by === "days" ? d.days : d.tabs) : [];
  const max = Math.max(0, ...rows.map((r) => r.added));
  const seg = (on: boolean) =>
    `rounded-[6px] px-2.5 py-1 text-[12.5px] font-medium ${on ? "bg-accent-dim text-accent-text" : "text-fg-muted hover:bg-bg-hover hover:text-fg"}`;
  return (
    <section className="flex flex-col gap-3" data-annot="активность моста">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h3 className="text-[14px] font-semibold">Активность моста</h3>
        <DateRangePicker label="Период" from={from} to={to} onChange={(a, b) => set({ bfrom: a, bto: b })} />
        <div role="group" aria-label="Разрез" className="ml-auto flex gap-1">
          <button type="button" aria-pressed={by === "days"} onClick={() => set({ bby: "" })} className={seg(by === "days")}>
            по дням
          </button>
          <button type="button" aria-pressed={by === "tabs"} onClick={() => set({ bby: "tabs" })} className={seg(by === "tabs")}>
            по вкладкам
          </button>
        </div>
      </div>
      {q.isError && <div className="text-[13px] text-err">{q.error.message}</div>}
      {q.isPending && <div className="text-[13px] text-fg-muted">Загрузка…</div>}
      {d && (
        <>
          <div className="flex flex-wrap gap-3">
            <Tile
              label="Получено: мост принёс в «Чаты»"
              value={d.total.added}
              hint={`Новые сообщения, которые завела вкладка моста: от клиентов и наши, набранные прямо в браузере, и рассылки. ${БЕЗ_РАЗБИВКИ}`}
              sub={[
                d.total.in + d.total.out > 0 && `от клиентов ${тыс(d.total.in)} · наших ${тыс(d.total.out)}`,
                d.total.unsplit > 0 && `без разбивки ${тыс(d.total.unsplit)} (до 21.10)`,
              ]
                .filter(Boolean)
                .join(" · ")}
            />
            <Tile
              label="Отправлено из «Чатов» через мост"
              value={d.total.sent}
              hint="Ответы сотрудников из «Чатов», набранные во вкладке моста и увиденные в переписке площадки"
              sub={d.total.sent + d.total.failed ? `ушло ${Math.round((100 * d.total.sent) / (d.total.sent + d.total.failed))} % попыток` : undefined}
            />
            <Tile label="Не ушло или не подтверждено" value={d.total.failed} bad hint="Ответы из «Чатов», которые через мост не ушли или не подтвердились — разбор ниже, в «Ответах из «Чатов»»" />
          </div>
          <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
            <table className="w-full text-left text-[12.5px]" data-annot={by === "days" ? "активность моста по дням" : "активность моста по вкладкам"}>
              <thead className={head}>
                <tr>
                  {by === "days" ? (
                    <th className={th}>День</th>
                  ) : (
                    <>
                      <th className={th}>Браузер · номер</th>
                      <th className={th}>Вкладка</th>
                    </>
                  )}
                  <CountsHead />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {by === "days"
                  ? d.days.map((r) => (
                      <tr key={r.date}>
                        <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-fg-muted">{деньСтроки(r.date)}</td>
                        <Counts c={r} max={max} />
                      </tr>
                    ))
                  : d.tabs.map((r) => (
                      <tr key={`${r.port}${r.channel}`}>
                        <td className="whitespace-nowrap px-3 py-1.5">
                          {r.port || (
                            <span className="text-fg-muted" title="Мост, которого больше нет (переустановлен с новым id), или аккаунт вне реестра браузеров">
                              прежний мост
                            </span>
                          )}
                          {r.title && <span className="font-semibold"> · {r.title}</span>}
                        </td>
                        <td className="whitespace-nowrap px-3 py-1.5">{ПЛОЩАДКА[r.channel] ?? r.channel}</td>
                        <Counts c={r} max={max} />
                      </tr>
                    ))}
                {by === "tabs" && d.tabs.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-2 text-fg-muted">
                      За период мост ничего не принёс и не отправлял.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot className="border-t border-border bg-bg-subtle font-semibold">
                <tr>
                  <td className="px-3 py-1.5" colSpan={by === "days" ? 1 : 2}>
                    Итого
                  </td>
                  <Counts c={d.total} max={0} />
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

/** Сводка над таблицей: сколько вкладок несут сообщения, какие номера вылетели, до каких браузеров кабинет не достаёт. */
function Logins({ rows }: { rows: ExtBridgeRow[] }) {
  const tabs = rows.filter(вРаботе);
  const out = tabs.filter(безВхода);
  const lost = out.filter((r) => r.was_seen);
  const never = out.filter((r) => !r.was_seen);
  const unused = rows.filter((r) => r.channel && r.unused);
  const down = rows.filter((r) => r.port_down);
  if (tabs.length === 0 && unused.length === 0) return null;
  const где = (r: ExtBridgeRow) => `${ПЛОЩАДКА[r.channel] ?? r.channel} ${порт(r)}`;
  return (
    <div className={`flex flex-col gap-1 text-[13px] ${out.length || down.length ? "text-err" : "text-fg-muted"}`} data-annot="входы на вкладках мостов">
      <div className="font-semibold">
        Сообщения несут {tabs.length - out.length} из {tabs.length} вкладок{out.length ? ` — на ${out.length} нет входа, приём с них не идёт` : ""}
      </div>
      {lost.length > 0 && <div>Вход пропал: {lost.map((r) => `${где(r)} (${номер(r.was_title, r.was_phone)}, видели ${when(r.was_seen)})`).join("; ")}</div>}
      {never.length > 0 && <div>Не входили: {never.map(где).join("; ")}</div>}
      {down.length > 0 && (
        <div>
          Кабинет не достаёт до браузеров {down.map(порт).join(", ")} (порт не ответил на проверке {when(down.map((r) => r.port_down).sort().pop() ?? "")}): ответы из
          «Чатов», рассылки и сторож через них стоят, входы ниже — со слов самих вкладок.
        </div>
      )}
      {unused.length > 0 && <div className="text-fg-muted">Не используем: {unused.map(где).join("; ")}</div>}
    </div>
  );
}

/** Переключатель строки таблицы: состояние словами рядом, пока запрос идёт — показывает то, что нажали. */
function Switch({ on, label, hint, words, save }: { on: boolean; label: string; hint: string; words: [string, string]; save: (next: boolean) => Promise<unknown> }) {
  const client = useQueryClient();
  const set = useMutation({ mutationFn: save, onSettled: () => client.invalidateQueries({ queryKey: ["ext-bridge-journal"] }) });
  const now = set.isPending ? !!set.variables : on;
  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        role="switch"
        aria-checked={now}
        aria-label={label}
        title={hint}
        disabled={set.isPending}
        onClick={() => set.mutate(!now)}
        className={`relative h-[18px] w-[32px] shrink-0 rounded-full transition-colors disabled:opacity-60 ${now ? "bg-ok" : "bg-fg-faint"}`}
      >
        <span className={`absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white transition-[left] ${now ? "left-[16px]" : "left-[2px]"}`} />
      </button>
      <span className={now ? "text-ok" : "text-fg-muted"}>{words[now ? 0 : 1]}</span>
      {set.isError && <span className="text-err">{set.error.message}</span>}
    </span>
  );
}

function Bridges({ rows }: { rows: ExtBridgeRow[] }) {
  if (rows.length === 0) return <div className="text-[13px] text-fg-muted">Мостов нет: расширение ещё не ставили.</div>;
  /** Какие наши номера живут в браузере — по его вкладкам, вошедшим и вылетевшим. */
  const номера = (install: string) => [...new Set(rows.filter((r) => r.install === install && r.channel).map((r) => r.title || r.was_title).filter(Boolean))].join(", ");
  return (
    <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
      <table className="w-full text-left text-[12.5px]" data-annot="мосты и вкладки">
        <thead className={head}>
          <tr>
            <th className={th}>Браузер</th>
            <th className={th}>Вкладка</th>
            <th className={th} title="Используем ли эту площадку в этом браузере. Нет — вкладка не слушает, потерей входа не считается, писать через неё нельзя.">
              Используем
            </th>
            <th className={th}>Номер</th>
            <th className={th}>Приём</th>
            <th className={th} title="Можно ли из «Чатов» писать клиентам через эту вкладку. Приём сообщений от этого не зависит.">
              Писать из «Чатов»
            </th>
            <th className={th}>Версия</th>
            <th className={th}>На связи</th>
            <th className={th}>Что сообщила</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => {
            const min = agoMin(r.seen);
            const silent = r.listen && !r.unused && min > 2;
            const площадка = ПЛОЩАДКА[r.channel] ?? r.channel;
            const беда = !r.unused && (silent || !!r.note || безВхода(r) || !!r.port_down);
            return (
              <tr key={`${r.install}${r.channel}`} className={беда ? "bg-err-bg" : ""}>
                <td className="whitespace-nowrap px-3 py-1.5">
                  {!r.channel && (
                    <>
                      {r.name || r.install}
                      {номера(r.install) && <span className="font-semibold"> · {номера(r.install)}</span>}
                    </>
                  )}
                </td>
                <td className={`whitespace-nowrap px-3 py-1.5 ${r.unused ? "text-fg-faint" : ""}`}>{r.channel && площадка}</td>
                <td className="whitespace-nowrap px-3 py-1.5">
                  {r.channel && (
                    <Switch
                      on={!r.unused}
                      label={`Используем ${площадка} в браузере ${порт(r)}`}
                      hint={r.unused ? "Площадку в этом браузере не используем — нажмите, чтобы вернуть в работу" : "Нажмите, если эту площадку в этом браузере не используем"}
                      words={["да", "не используем"]}
                      save={(use) => setExtBridgeUse(r.install, r.channel, use)}
                    />
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-fg-muted">
                  {!r.channel ? null : r.account_id ? (
                    <>
                      {r.title && <span className="text-fg">«{r.title}» </span>}
                      {r.account_phone ? `+${r.account_phone}` : `id ${r.account_id}`}
                    </>
                  ) : r.unused ? (
                    <span className="text-fg-faint">—</span>
                  ) : (
                    <span className="text-err">{r.was_seen ? `нет входа · был ${номер(r.was_title, r.was_phone)}, видели ${when(r.was_seen)}` : "нет входа · не входили"}</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-1.5">
                  {!r.channel ? (
                    r.token ? "" : <span className="text-err">токен отозван</span>
                  ) : r.unused ? (
                    <span className="text-fg-faint">—</span>
                  ) : !r.listen ? (
                    <span className="text-fg-faint">выключен</span>
                  ) : r.account_id ? (
                    <span className="text-ok">идёт</span>
                  ) : (
                    <span className="font-semibold text-err">не идёт</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-1.5">
                  {!r.channel ? null : r.unused || r.send === null ? (
                    <span className="text-fg-faint" title={r.unused ? "Площадку в этом браузере не используем" : "Вкладки нет в реестре браузеров"}>
                      —
                    </span>
                  ) : (
                    <Switch
                      on={r.send}
                      label={`Писать клиентам из «Чатов» через ${площадка} браузера ${порт(r)}`}
                      hint={r.send ? "Из «Чатов» в этот номер писать можно — нажмите, чтобы запретить" : "Писать через эту вкладку запрещено — нажмите, чтобы разрешить"}
                      words={["можно", "запрещено"]}
                      save={(send) => setExtBridgeSend(r.port ?? 0, r.channel, send)}
                    />
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-fg-muted">{r.version}</td>
                <td className={`whitespace-nowrap px-3 py-1.5 tabular-nums ${silent ? "font-semibold text-err" : "text-fg-muted"}`}>
                  {min < 1 ? "сейчас" : min < 120 ? `${min} мин назад` : when(r.seen)}
                  {silent && " — молчит"}
                </td>
                <td className="px-3 py-1.5 text-err">
                  {r.note}
                  {r.port_down && `порт браузера не отвечает кабинету (проверка ${when(r.port_down)})`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Полоса шагов: ширина деления — доля времени шага; соседние различаются плотностью цвета. */
function Timeline({ send }: { send: ExtBridgeSend }) {
  const total = Math.max(1, send.steps.reduce((sum, s) => sum + s.ms, 0));
  if (send.steps.length === 0) return <span className="text-fg-faint">шагов нет</span>;
  return (
    <div className="flex h-3 w-[220px] gap-px overflow-hidden rounded-[3px]" role="img" aria-label={send.steps.map((s) => `${s.label} ${sec(s.ms)}`).join(", ")}>
      {send.steps.map((s, i) => (
        <span
          key={`${s.step}${i}`}
          title={`${s.label} — ${sec(s.ms)}`}
          style={{ flexGrow: Math.max(s.ms, total / 60) }}
          className={`min-w-[2px] basis-0 ${send.state === "sent" ? "bg-accent" : "bg-err"} ${i % 2 ? "opacity-45" : "opacity-85"}`}
        />
      ))}
    </div>
  );
}

function SendRow({ s }: { s: ExtBridgeSend }) {
  const [open, setOpen] = useState(false);
  const out = ИСХОД[s.state] ?? НАБИРАЕТСЯ;
  return (
    <>
      <tr className="cursor-pointer align-middle hover:bg-bg-hover" onClick={() => setOpen(!open)}>
        <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-fg-muted">{when(s.date)}</td>
        <td className="whitespace-nowrap px-3 py-1.5">
          <Link to={`/chats/${s.chat}`} className="text-accent-text hover:underline" onClick={(e) => e.stopPropagation()}>
            {s.name || `чат ${s.chat}`}
          </Link>
          <span className="text-fg-faint"> · {ПЛОЩАДКА[s.channel] ?? s.channel}{s.account ? ` «${s.account}»` : ""}</span>
        </td>
        <td className="whitespace-nowrap px-3 py-1.5 text-fg-muted">{s.user}</td>
        <td className="whitespace-nowrap px-3 py-1.5">
          <span className={`rounded-full border px-2 py-0.5 text-[12px] font-medium ${out.tone}`}>{out.label}</span>
        </td>
        <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">{s.ms ? sec(s.ms) : "—"}</td>
        <td className="px-3 py-1.5">
          <Timeline send={s} />
        </td>
        <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">
          {s.bridge_ms === null ? (
            <span className="text-fg-faint">{s.state === "sent" && s.steps.length > 0 ? "не приносил" : "—"}</span>
          ) : (
            <span className="text-ok">принёс {s.bridge_ms < 0 ? "раньше набора" : `через ${sec(s.bridge_ms)}`}</span>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} className="bg-bg-subtle px-3 pb-3 pt-2">
            <div className="flex flex-wrap gap-x-10 gap-y-3 text-[12.5px]">
              <table className="self-start tabular-nums" data-annot="шаги ответа">
                <tbody>
                  {s.steps.map((st, i) => (
                    <tr key={`${st.step}${i}`}>
                      <td className="pr-3 text-right text-fg-faint">{sec(st.at)}</td>
                      <td className="pr-3">
                        {st.label}
                        {st.chars ? <span className="text-fg-faint"> · {st.chars} зн.</span> : null}
                      </td>
                      <td className="text-right text-fg-muted">{sec(st.ms)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex max-w-[520px] flex-col gap-1">
                <div>
                  Ответ {s.id} · {s.len} зн. · {out.label}
                  {s.state === "sent" && s.tick ? ` · галка «${s.tick}»` : ""}
                </div>
                {s.error && <div className="text-err">{s.error}</div>}
                {(s.notes ?? []).map((note, i) => (
                  <div key={`${note.step}${i}`} className="text-fg-muted" data-annot="заметка набора">
                    <span className="tabular-nums text-fg-faint">{sec(note.at)}</span> {note.label}: {note.text}
                  </div>
                ))}
                {s.by_bridge && (
                  <div className="text-warn">
                    Набор отправку не подтвердил: своё сообщение в переписке страницы он не дождался. Подтвердил мост — принёс это
                    сообщение сам.
                  </div>
                )}
                {s.shot && (
                  <a href={extBridgeShot(s.id)} target="_blank" rel="noreferrer" className="block w-fit" title="Что было на экране вкладки в момент отказа — открыть в полном размере">
                    <img src={extBridgeShot(s.id)} alt="Снимок вкладки в момент отказа" loading="lazy" className="max-h-[220px] rounded-[var(--radius-ui)] border border-border" />
                  </a>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/** Событие словами. */
function words(e: ExtBridgeEvent): string {
  const d = e.data;
  switch (e.code) {
    case "stat": {
      const lag = n(d["lag_n"]) ? ` · задержка приёма ${sec(n(d["lag_ms"]) / n(d["lag_n"]))}, до ${sec(n(d["lag_max_ms"]))}` : "";
      const api = n(d["api_n"]) ? ` · запросов ${n(d["api_n"])}${n(d["api_fail"]) ? `, отказов ${n(d["api_fail"])}` : ""}, ответ ${sec(n(d["api_ms"]) / n(d["api_n"]))}` : "";
      const got = n(d["messages"]) + n(d["catchup_messages"]) + n(d["frames"]);
      const что = n(d["frames"]) ? `кадров ${n(d["frames"])}` : `сообщений ${n(d["messages"])}${n(d["catchup_messages"]) ? ` (+${n(d["catchup_messages"])} догоном)` : ""}`;
      return `За минуту: ${got ? `${что}, новых в «Чатах» ${n(d["added"])}` : "сообщений не было"}${lag}${api}${d["visible"] === false ? " · вкладка в фоне" : ""}`;
    }
    case "start":
      return `Вкладка моста загружена${d["visible"] === false ? " (в фоне)" : ""}`;
    case "sw_start":
      return "Расширение запущено (service worker)";
    case "account":
      return `Вход во вкладке: аккаунт ${String(d["account"] ?? "")}`;
    case "api_fail":
      return `Кабинет не принял запрос — ${e.text}`;
    case "listen_fail":
      return `Страница не дала подписку на сообщения — ${e.text}`;
    case "file_fail":
      return `Файл не скачан — ${e.text}`;
    default:
      return e.text ? (/^(page_|tg_|wa_|sw_|.*_crash$)/.test(e.code) ? `Сбой (${e.code}) — ${e.text}` : e.text.charAt(0).toUpperCase() + e.text.slice(1)) : e.code;
  }
}
const УРОВЕНЬ = { info: "text-fg", warn: "text-warn", error: "font-semibold text-err" };

function EventRow({ e }: { e: ExtBridgeEvent }) {
  const [open, setOpen] = useState(false);
  return (
    <Fragment>
      <tr className={`cursor-pointer align-top hover:bg-bg-hover ${e.level === "error" ? "bg-err-bg" : ""}`} onClick={() => setOpen(!open)}>
        <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-fg-muted">{when(e.at)}</td>
        <td className="whitespace-nowrap px-3 py-1.5 text-fg-muted">
          {e.name || e.install} · {ПЛОЩАДКА[e.channel] ?? e.channel}
        </td>
        <td className={`px-3 py-1.5 ${e.code === "stat" ? "text-fg-muted" : УРОВЕНЬ[e.level]}`}>
          {words(e)}
          {e.count > 1 ? <span className="text-fg-faint"> ×{e.count}</span> : null}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={3} className="px-3 pb-2">
            <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-[var(--radius-ui)] bg-bg-subtle p-2 font-mono text-[12px] text-fg-muted">
              {JSON.stringify({ code: e.code, level: e.level, version: e.version, account: e.account_id, text: e.text, ...e.data }, null, 1)}
            </pre>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

export function BridgeJournal() {
  const [errors, setErrors] = useState(false);
  const [hours, setHours] = useState(24);
  const q = useQuery({
    queryKey: ["ext-bridge-journal", errors, hours],
    queryFn: () => loadExtBridgeJournal(errors, hours),
    refetchInterval: 15_000,
    placeholderData: (prev) => prev,
  });
  const sends = q.data?.sends ?? [];
  const events = q.data?.events ?? [];
  const sent = sends.filter((s) => s.state === "sent");
  const bad = sends.filter((s) => s.state === "failed" || s.state === "unknown").length;
  const avg = sent.length ? sent.reduce((sum, s) => sum + s.ms, 0) / sent.length : 0;
  const period = ПЕРИОД.find((p) => p.hours === hours)?.label ?? `${hours} ч`;
  return (
    <div className="flex flex-col gap-5">
      <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4 text-[13.5px]" data-annot="что в журнале моста">
        <div className="font-semibold">Как работает мост и ответ из «Чатов» через служебные браузеры</div>
        <div className="mt-1 text-fg-muted">
          Расширение «Прокат — мост» стоит в служебных браузерах с нашими номерами: приносит в «Чаты» сообщения всех
          диалогов и само сообщает сюда об ошибках. Ответ из «Чатов» набирается в диалоге вкладки — у каждого ответа
          записаны шаги и время каждого. Текста переписки в журнале нет. Нажмите на строку — подробности.
        </div>
      </div>
      <Activity />
      {q.isError && <div className="text-[13px] text-err">{q.error.message}</div>}
      {q.isPending && <div className="text-[13px] text-fg-muted">Загрузка…</div>}
      {q.data && (
        <>
          <section className="flex flex-col gap-2">
            <h3 className="text-[14px] font-semibold">Мосты и вкладки</h3>
            <Logins rows={q.data.bridges} />
            <Bridges rows={q.data.bridges} />
          </section>

          <section className="flex flex-col gap-2" data-annot="ответы через служебные браузеры">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h3 className="text-[14px] font-semibold">Ответы из «Чатов»</h3>
              <span className="text-[12px] text-fg-faint">
                за {period}: ушло {sent.length}
                {sent.length ? ` (в среднем ${sec(avg)})` : ""}, не ушло или не подтверждено{" "}
                <span className={bad ? "font-semibold text-err" : ""}>{bad}</span>
              </span>
              <div role="group" aria-label="Период" className="ml-auto flex gap-1">
                {ПЕРИОД.map((p) => (
                  <button
                    key={p.hours}
                    type="button"
                    aria-pressed={hours === p.hours}
                    onClick={() => setHours(p.hours)}
                    className={`rounded-[6px] px-2.5 py-1 text-[12.5px] font-medium ${hours === p.hours ? "bg-accent-dim text-accent-text" : "text-fg-muted hover:bg-bg-hover hover:text-fg"}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            {sends.length === 0 ? (
              <div className="text-[13px] text-fg-muted">За {period} из «Чатов» через служебные браузеры не отвечали.</div>
            ) : (
              <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
                <table className="w-full text-left text-[12.5px]">
                  <thead className={head}>
                    <tr>
                      <th className={th}>Когда</th>
                      <th className={th}>Чат</th>
                      <th className={th}>Кто</th>
                      <th className={th}>Исход</th>
                      <th className={`${th} text-right`}>Всего</th>
                      <th className={th}>Шаги: диалог → набор → отправка → проверка</th>
                      <th className={th}>Мост</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {sends.map((s) => (
                      <SendRow key={s.id} s={s} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="flex flex-col gap-2" data-annot="события моста">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h3 className="text-[14px] font-semibold">События расширения</h3>
              <span className="text-[12px] text-fg-faint">новые сверху · {events.length} строк</span>
              <label className="ml-auto flex cursor-pointer items-center gap-1.5 text-[12.5px] text-fg-muted">
                <input type="checkbox" checked={errors} onChange={(e) => setErrors(e.target.checked)} />
                только отказы и ошибки
              </label>
            </div>
            {events.length === 0 ? (
              <div className="text-[13px] text-fg-muted">{errors ? `Ошибок за ${period} нет.` : `За ${period} событий нет.`}</div>
            ) : (
              <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
                <table className="w-full text-left text-[12.5px]">
                  <thead className={head}>
                    <tr>
                      <th className={th}>Когда</th>
                      <th className={th}>Где</th>
                      <th className={th}>Что произошло</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {events.map((e) => (
                      <EventRow key={e.id} e={e} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
